import PropTypes from "prop-types";
import { Box, Flex, Image, Text, useMantineTheme } from "@mantine/core";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { BorderedContent } from "./BorderedContent";
import React from "react";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import {
  formatTimeStamp,
  LONG_DISPLAY_FORMAT,
  SHORT_DISPLAY_FORMAT,
} from "../utils/collectionFound/dateTimeUtils";
import { formatHexStr } from "../utils/formatHexStr";
import {
  hasIdentityDowngradedSinceStamp,
  hasUserChangedSinceStamp,
} from "../utils/stampIdentity";
import { StampChangeFlag } from "./StampIdentityFields";

const StampDetail = ({
  helpIsActive,
  data,
  staticBasePath,
  timeZone,
  id = "",
}) => {
  //css classes
  const { classes: tooltipClasses } = useStylesTooltip();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // Use the parent's resolved zone; the raw preference could show local time labeled UTC.
  const formattedDateTime = formatTimeStamp(
    data.timestamp,
    timeZone,
    LONG_DISPLAY_FORMAT,
  );
  const user = data.user || data.user_id || "";
  const userHref = user
    ? `/verify/user-data/?user=${encodeURIComponent(user)}`
    : "#";
  const collectionNameHref =
    user && data.collection_name
      ? `${userHref}&collection_name=${encodeURIComponent(
          data.collection_name,
        )}`
      : "#";
  const collectionHashHref =
    user && data.collection_hash
      ? `${userHref}&collection_cid=${encodeURIComponent(data.collection_hash)}`
      : "#";

  return (
    <BorderedContent id={id}>
      <Box className="details">
        {helpIsActive && (
          <Box className="tooltip">
            <CustomTooltip
              content={
                <>
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    <Text component="span" className="b">
                      Timestamp
                    </Text>{" "}
                    is an emergent property of when the data fingerprint was
                    added to the blockchain.
                  </Text>
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    <Text component="span" className="b">
                      User
                    </Text>{" "}
                    is the chosen account name or account ID of the user who
                    made the stamp. In blockchain terms, an account ID is a
                    wallet address.
                  </Text>
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    <Text component="span" className="b">
                      User Identity
                    </Text>{" "}
                    indicates whether vBase has confirmed the identity of the
                    user.
                  </Text>

                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    <Text component="span" className="b">
                      Collections
                    </Text>{" "}
                    are assigned groups of stamps, useful for creating
                    verifiable data across topics.
                  </Text>
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    <Text component="span" className="b">
                      You don’t need to save any of the information on this page
                    </Text>{" "}
                    — just keep the file. The file is all you need to validate
                    this information later.
                  </Text>
                </>
              }
            ></CustomTooltip>
          </Box>
        )}
        <Flex gap={theme.flex.gap.xxs} className="date">
          <Text component="span" className="title">
            Timestamp
          </Text>
          <Text component="span" className="timestamp">
            {formattedDateTime} {timeZone}
          </Text>
          <Text component="span" className="time-delta">
            {" "}
            {data.timedelta}
          </Text>
        </Flex>

        <Flex className="info" justify={theme.flex.justify.between}>
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              User
            </Text>

            <Flex className="stamp-user-value-group">
              <CustomAnchor
                text={user}
                componentClasses="value stamp-link stamp-user"
                color={theme.customColors.black}
                href={userHref}
              />
              {hasUserChangedSinceStamp(data) && (
                <StampChangeFlag>Different name at stamp time</StampChangeFlag>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex className="info" justify={theme.flex.justify.between}>
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              User Identity
            </Text>

            {data.verified ? (
              <Flex
                gap={theme.flex.gap.xs}
                align={theme.flex.align.center}
                className="value"
              >
                <Image
                  src={`${staticBasePath}public/images/svg/verified.svg`}
                  alt="verified"
                />
                <Text className="user-info-status">
                  Verified{" "}
                  {data.verified_since && (
                    <Text component="span">
                      (since{" "}
                      {formatTimeStamp(
                        data.verified_since,
                        timeZone,
                        SHORT_DISPLAY_FORMAT,
                      )}
                      )
                    </Text>
                  )}
                </Text>
                {hasIdentityDowngradedSinceStamp(data) && (
                  <StampChangeFlag>
                    Different identity at stamp time
                  </StampChangeFlag>
                )}
              </Flex>
            ) : (
              <Flex
                gap={theme.flex.gap.xs}
                align={theme.flex.align.center}
                className="value"
              >
                <Text className="user-info-status">Unverified</Text>
                {hasIdentityDowngradedSinceStamp(data) && (
                  <StampChangeFlag>
                    Different identity at stamp time
                  </StampChangeFlag>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>

        <Flex className="info" justify={theme.flex.justify.between}>
          <Flex gap={theme.flex.gap.xxs}>
            <Text className="key" component="span">
              Collection
            </Text>
            {data.collection_name ? (
              <CustomAnchor
                text={data.collection_name}
                componentClasses="value collection-name stamp-link"
                color={theme.customColors.black}
                href={collectionNameHref}
              />
            ) : data.collection_hash ? (
              <CustomAnchor
                text={formatHexStr(data.collection_hash)}
                componentClasses="value collection-name stamp-link"
                color={theme.customColors.black}
                href={collectionHashHref}
              />
            ) : (
              <Text component="span" className="value collection-name">
                {"<<None>>"}
              </Text>
            )}
          </Flex>
        </Flex>
      </Box>
    </BorderedContent>
  );
};

StampDetail.propTypes = {
  data: PropTypes.object.isRequired,
  staticBasePath: PropTypes.string.isRequired,
  helpIsActive: PropTypes.bool,
  timeZone: PropTypes.string.isRequired,
  id: PropTypes.string,
};
export default StampDetail;
